/* Grievance Dashboard — the numbers HR and an auditor both ask for.

   Scoped server-side to whatever the caller may read, so this renders for an
   org admin (whole company), a category handler (their queue) and a plain
   employee (their own record) without a branch here. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle, BarChart3, CheckCircle2, Clock, Gauge, Inbox, RotateCcw, Timer,
} from "lucide-react";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { cn } from "../../utils/tailwindUtils";
import { GRIEVANCE_DASHBOARD } from "../../constants/api";
import { GRIEVANCE_STATUS_LABELS } from "../../constants/grievance";
import { fmtDate, PriorityBadge, SectionLabel, StatusBadge } from "./GrievancePieces";

const PERIODS = [
    { value: "30", label: "30 days" },
    { value: "90", label: "90 days" },
    { value: "365", label: "1 year" },
];

const Tile = ({ Icon, label, value, suffix, tone = "default", hint }) => (
    <div className="rounded-lg border border-gray-200 bg-white p-3.5 dark:border-gray-700 dark:bg-gray-800/40">
        <div className="flex items-center gap-2">
            <span
                className={cn(
                    "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                    tone === "danger"
                        ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300"
                        : tone === "success"
                            ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300"
                            : "bg-sidebar-primary/10 text-sidebar-primary",
                )}
            >
                <Icon className="h-4 w-4" />
            </span>
            <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
        </div>
        <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
            {value === null || value === undefined ? "—" : value}
            {value !== null && value !== undefined && suffix && (
                <span className="ml-1 text-sm font-normal text-muted-foreground">{suffix}</span>
            )}
        </p>
        {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
);

/* A plain CSS bar chart. The rest of the app reaches for chart.js on richer
   screens, but a labelled distribution of at most a dozen rows reads better —
   and stays legible in both themes — as bars in the flow of the page. */
const DistributionBars = ({ rows, total, colorFor }) => {
    if (!rows.length) return <p className="text-sm text-muted-foreground">No grievances yet.</p>;
    return (
        <div className="space-y-2">
            {rows.map((row) => {
                const pct = total ? Math.round((row.count * 100) / total) : 0;
                return (
                    <div key={row.key} className="flex items-center gap-3">
                        <span className="w-44 shrink-0 truncate text-xs text-gray-700 dark:text-gray-300">
                            {row.label}
                        </span>
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                            <div
                                className={cn("h-full rounded-full", colorFor(row))}
                                style={{ width: `${Math.max(pct, row.count ? 2 : 0)}%` }}
                            />
                        </div>
                        <span className="w-16 shrink-0 text-right text-xs font-medium text-gray-700 dark:text-gray-300">
                            {row.count} · {pct}%
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const PRIORITY_BAR = {
    low: "bg-gray-400",
    medium: "bg-blue-500",
    high: "bg-orange-500",
    critical: "bg-red-500",
};

const GrievanceDashboard = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [days, setDays] = useState("90");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(GRIEVANCE_DASHBOARD, { days });
            if (res?.status === "success") setData(res.data);
            else ToastMsg("error", res?.message || "Could not load the dashboard");
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const tiles = data?.tiles || {};
    const total = tiles.total || 0;

    const statusRows = (data?.by_status || []).map((r) => ({
        key: r.status,
        label: r.status_label || GRIEVANCE_STATUS_LABELS[r.status] || r.status,
        count: r.count,
        status: r.status,
    }));
    const categoryRows = (data?.by_category || []).map((r) => ({
        key: String(r.category_id ?? "none"),
        label: r.category_name,
        count: r.count,
        isSensitive: r.is_sensitive,
    }));
    const priorityRows = (data?.by_priority || []).map((r) => ({
        key: r.priority,
        label: (r.priority || "").replace(/^./, (c) => c.toUpperCase()),
        count: r.count,
        priority: r.priority,
    }));

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={BarChart3}
                title="Grievance Dashboard"
                subtitle="Volume, ageing and SLA compliance across the grievances you can see"
            />

            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 px-3">
                <SegmentSwitch options={PERIODS} value={days} onChange={setDays} />
                <Button variant="outline" size="sm" onClick={() => navigate("/grievance/inbox")}>
                    <Inbox className="h-4 w-4" /> Open inbox
                </Button>
            </div>

            {loading && !data ? (
                <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
            ) : (
                <div className="space-y-4 px-3 pb-6">
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <Tile Icon={BarChart3} label="Total" value={tiles.total} />
                        <Tile Icon={Clock} label="Open now" value={tiles.open} />
                        <Tile
                            Icon={AlertTriangle}
                            label="Overdue"
                            value={tiles.breached}
                            tone={tiles.breached ? "danger" : "default"}
                            hint={tiles.breached ? "Past acknowledgement or resolution SLA" : "Nothing past SLA"}
                        />
                        <Tile
                            Icon={CheckCircle2}
                            label="Resolved"
                            value={tiles.resolved_this_period}
                            tone="success"
                            hint={`In the last ${data?.period_days ?? days} days`}
                        />
                        <Tile
                            Icon={Timer}
                            label="Avg resolution"
                            value={tiles.avg_resolution_days}
                            suffix="days"
                        />
                        <Tile
                            Icon={Gauge}
                            label="SLA compliance"
                            value={tiles.sla_compliance_percent}
                            suffix="%"
                            tone={
                                tiles.sla_compliance_percent === null || tiles.sla_compliance_percent === undefined
                                    ? "default"
                                    : tiles.sla_compliance_percent >= 90 ? "success" : "danger"
                            }
                            hint="Resolved on or before the due date"
                        />
                        <Tile
                            Icon={RotateCcw}
                            label="Reopen rate"
                            value={tiles.reopen_rate_percent}
                            suffix="%"
                            hint="High means resolutions are closing too early"
                        />
                        <Tile Icon={Inbox} label="My grievances open" value={tiles.my_open} />
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <div className="rounded-lg border border-gray-200 p-3.5 dark:border-gray-700">
                            <SectionLabel>By status</SectionLabel>
                            <DistributionBars
                                rows={statusRows}
                                total={total}
                                colorFor={(row) =>
                                    ["escalated", "reopened"].includes(row.status)
                                        ? "bg-rose-500"
                                        : ["resolved", "closed"].includes(row.status)
                                            ? "bg-emerald-500"
                                            : "bg-sidebar-primary"
                                }
                            />
                        </div>

                        <div className="rounded-lg border border-gray-200 p-3.5 dark:border-gray-700">
                            <SectionLabel>By category</SectionLabel>
                            <DistributionBars
                                rows={categoryRows}
                                total={total}
                                colorFor={(row) => (row.isSensitive ? "bg-rose-500" : "bg-indigo-500")}
                            />
                        </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 p-3.5 dark:border-gray-700">
                        <SectionLabel>By priority</SectionLabel>
                        <DistributionBars
                            rows={priorityRows}
                            total={total}
                            colorFor={(row) => PRIORITY_BAR[row.priority] || "bg-blue-500"}
                        />
                    </div>

                    <div className="rounded-lg border border-gray-200 p-3.5 dark:border-gray-700">
                        <SectionLabel>Needs attention</SectionLabel>
                        {(data?.attention || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                Nothing is past its SLA. Good place to be.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[640px] text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-100 text-left dark:border-gray-800">
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Grievance</th>
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Category</th>
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Priority</th>
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Status</th>
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Handler</th>
                                            <th className="pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Due</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.attention.map((row) => (
                                            <tr
                                                key={row.id}
                                                className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-800/40"
                                                onClick={() => navigate("/grievance/inbox")}
                                            >
                                                <td className="py-2">
                                                    <span className="font-semibold text-sidebar-primary">{row.code}</span>
                                                    <span className="ml-2 text-muted-foreground">{row.subject}</span>
                                                </td>
                                                <td className="py-2">{row.category_name || "—"}</td>
                                                <td className="py-2"><PriorityBadge priority={row.priority} /></td>
                                                <td className="py-2"><StatusBadge status={row.status} /></td>
                                                <td className="py-2">{row.assigned_to_name || "Unassigned"}</td>
                                                <td className="py-2 text-xs text-red-600 dark:text-red-400">
                                                    {fmtDate(row.resolution_due_at)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default GrievanceDashboard;
